import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@design-system';
import {
  CARE_AMOUNTS,
  CONSUMER_CARE_SETTINGS,
  FAMILY_HELP_OPTIONS,
  type CareAdjustments,
  type CareAmount,
  type FamilyHelp,
} from '@shared/consumerPlan';
import { CARE_AMOUNT_COPY, CARE_SETTING_COPY, FAMILY_HELP_COPY } from '../lib/copy';
import { hoursPerWeek } from '../lib/format';
import { ChoiceList } from './choice-list';

interface CareAdjustmentChoicesProps {
  careSetting: ConsumerCareSetting | null;
  onCareSettingChange: (careSetting: ConsumerCareSetting) => void;
  adjustments: CareAdjustments;
  careHoursByAmount: Record<CareAmount, number>;
  familyHoursByHelp: Record<FamilyHelp, number>;
  onCareAmountChange: (careAmount: CareAmount) => void;
  onFamilyHelpChange: (familyHelp: FamilyHelp) => void;
}

function CareSettingText({ careSetting }: { careSetting: ConsumerCareSetting }) {
  return (
    <span className="flex flex-col gap-0.5 text-left">
      <span className="font-medium text-gray-950">{CARE_SETTING_COPY[careSetting].title}</span>
      <span className="text-muted-foreground text-xs whitespace-normal">{CARE_SETTING_COPY[careSetting].description}</span>
    </span>
  );
}

/** The optional overrides: where care happens, how much care to plan for, and how much of it family gives. */
export function CareAdjustmentChoices({
  careSetting,
  onCareSettingChange,
  adjustments,
  careHoursByAmount,
  familyHoursByHelp,
  onCareAmountChange,
  onFamilyHelpChange,
}: CareAdjustmentChoicesProps) {
  return (
    <div className="flex flex-col gap-6 text-gray-950">
      <p>Optional. If you’re not sure, keep our estimate and continue.</p>
      <div className="flex flex-col gap-2">
        <label
          htmlFor="consumer-care-setting"
          className="font-semibold"
        >
          Where would you receive care?
        </label>
        <Select
          value={careSetting ?? undefined}
          onValueChange={value => onCareSettingChange(value as ConsumerCareSetting)}
        >
          <SelectTrigger
            id="consumer-care-setting"
            className="w-full bg-white py-2 data-[size=default]:h-auto *:data-[slot=select-value]:line-clamp-none"
          >
            <SelectValue placeholder="Choose where care happens">
              {careSetting && <CareSettingText careSetting={careSetting} />}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {CONSUMER_CARE_SETTINGS.map(value => (
              <SelectItem
                key={value}
                value={value}
                className="py-2"
              >
                <CareSettingText careSetting={value} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 flex flex-col gap-1">
          <span className="font-semibold">How much care should we plan for?</span>
          <span className="text-muted-foreground">Adjust the amount of help you might need.</span>
        </legend>
        <ChoiceList
          label="Amount of care"
          value={adjustments.careAmount}
          onChange={onCareAmountChange}
          className="sm:grid-cols-3"
          choices={CARE_AMOUNTS.map(careAmount => ({
            value: careAmount,
            title: CARE_AMOUNT_COPY[careAmount],
            description: hoursPerWeek(careHoursByAmount[careAmount]),
          }))}
        />
      </fieldset>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 flex flex-col gap-1">
          <span className="font-semibold">How much help would family provide?</span>
          <span className="text-muted-foreground">
            Average hours a week across your spouse, children and other family, combined.
          </span>
        </legend>
        <ChoiceList
          label="Family involvement"
          value={adjustments.familyHelp}
          onChange={onFamilyHelpChange}
          className="sm:grid-cols-3"
          choices={FAMILY_HELP_OPTIONS.map(familyHelp => ({
            value: familyHelp,
            title: FAMILY_HELP_COPY[familyHelp],
            description: hoursPerWeek(familyHoursByHelp[familyHelp]),
          }))}
        />
      </fieldset>
    </div>
  );
}
